import { AdminMembersReportPageParams } from "@/admin/insights/member-reports/AdminMembersReportPage"
import AdminMembersReportSurveyAnswersTableExportCSVButton from "@/admin/insights/member-reports/tabs/contents/AdminMembersReportSurveyAnswersTableExportCSVButton"
import AdminMembersReportSurveysTable from "@/admin/insights/member-reports/tabs/contents/AdminMembersReportSurveysTable"
import MemberReportSurveyContentUsageSelect, {
  MemberReportSurveyContentUsage,
} from "@/admin/insights/member-reports/tabs/contents/MemberReportSurveyContentUsageSelect"
import { useLabel } from "@/core/context/LabelsContext"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoSection, DiscoSpinner, DiscoText } from "@disco-ui"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import React, { useEffect, useState } from "react"

function AdminMembersReportSurveysSection() {
  const [queryParams] = useQueryParamState<AdminMembersReportPageParams>()
  const classes = useStyles()
  const organizationLabel = useLabel("organization")

  const [selectedSurveyContentUsage, setSelectedSurveyContentUsage] =
    useState<MemberReportSurveyContentUsage | null>(null)

  useEffect(() => {
    resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.om!])

  return (
    <DiscoSection className={classes.container}>
      <div className={classes.header}>
        <DiscoText variant={"heading-xs-600"} className={classes.title}>
          {"Surveys"}
        </DiscoText>

        <AdminMembersReportSurveyAnswersTableExportCSVButton
          surveyContentUsageId={selectedSurveyContentUsage?.id}
        />
      </div>

      <div className={classes.selectorContainer}>
        <MemberReportSurveyContentUsageSelect
          values={selectedSurveyContentUsage ? [selectedSurveyContentUsage.id] : []}
          onChange={(result) => {
            const cu = result[0] || null
            setSelectedSurveyContentUsage(cu)
          }}
        />
      </div>

      {selectedSurveyContentUsage && (
        <>
          <div>
            <div>
              <DiscoText variant={"body-lg-600"} marginTop={2}>
                {selectedSurveyContentUsage.content.name}
              </DiscoText>
            </div>
            <div>
              <DiscoText variant={"body-sm"} color={"groovy.grey.500"} marginBottom={2}>
                {selectedSurveyContentUsage.module
                  ? selectedSurveyContentUsage.module.isCurriculumModule
                    ? `in Module: ${selectedSurveyContentUsage.module.name} • ${
                        selectedSurveyContentUsage.productName ||
                        organizationLabel.singular
                      }`
                    : `in Folder: ${selectedSurveyContentUsage.module.name} • ${
                        selectedSurveyContentUsage.productName ||
                        organizationLabel.singular
                      }`
                  : `in Dashboard • ${
                      selectedSurveyContentUsage.productName || organizationLabel.singular
                    }`}
              </DiscoText>
            </div>
          </div>
          <AdminMembersReportSurveysTable
            surveyContentUsageId={selectedSurveyContentUsage.id}
          />
        </>
      )}
    </DiscoSection>
  )

  function resetState() {
    setSelectedSurveyContentUsage(null)
  }
}

export const AdminMembersReportSurveysSectionSkeleton = () => {
  const classes = useStyles()
  return (
    <DiscoSection className={classes.root} padding={3}>
      <DiscoSpinner absoluteCenter />
    </DiscoSection>
  )
}

const useStyles = makeUseStyles((theme) => ({
  root: {
    position: "relative",
    height: 400,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  container: {
    border: theme.palette.constants.borderSmall,
    boxShadow: theme.palette.groovyDepths.xs,

    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },
  title: { width: "100%", fontWeight: 600 },
  selectorContainer: {
    width: "100%",
  },
}))

export default Relay.withSkeleton({
  component: React.memo(AdminMembersReportSurveysSection),
  skeleton: AdminMembersReportSurveysSectionSkeleton,
})
